// src/pages/pedidos/PedidosHistorial.js
import React, { useEffect, useState } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Alert from 'react-bootstrap/Alert'
import { toast } from 'react-toastify'
import api from 'api/api'

import {
	PEDIDOS_FETCH_CUANTOS,
	OPCIONES_TIPOS_PEDIDO_PARA_FILTRO,
	OPCIONES_PAGINACION_NUM_RESULTADOS,
	getTipoColor,
	calcularEstadoPrincipal,
	getConfigEstado,
	calcularEstados
} from 'config/pedidos'
import { debounce } from 'utils/debounce'
import { formatNumberForDisplay } from 'utils/numberUtils'
import { getDisplayDateTime } from 'utils/date'
import { normalizarBooleano } from 'utils/general'
import Paginacion from 'components/Paginacion'
import { LoadingSpinner } from 'components/LoadingSpinner'
import TopHeader from 'components/TopHeader'

import VerPedido from './VerPedido'

import './pedidos.css'

// Componente modal para descancelar pedido
function DescancelarPedido({ pedido, mostrar, cancelar, confirmarDescancelacion }) {
	const [loading, setLoading] = useState(false)

	const handleClose = () => {
		cancelar()
	}

	const handleConfirmar = async () => {
		try {
			setLoading(true)
			await confirmarDescancelacion(pedido.id)
		} catch (error) {
			console.error('Error al descancelar pedido:', error)
		} finally {
			setLoading(false)
		}
	}

	if (!pedido) return null

	return (
		<Modal show={mostrar} onHide={handleClose} backdrop="static" centered>
			<Modal.Header closeButton>
				<Modal.Title>
					<i className="fa fa-undo text-success me-2"></i>
					Descancelar Pedido
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Alert variant="info">
					<strong>¿Está seguro que desea descancelar el pedido #{pedido.id}?</strong>
				</Alert>

				{/* Información básica del pedido */}
				<div className="mb-3 p-3 bg-light rounded">
					<div className="row">
						<div className="col-6">
							<small className="text-muted">Cliente:</small><br />
							<strong>
								{pedido.usuario ?
									`${pedido.usuario.nombre} ${pedido.usuario.apellido}` :
									(pedido.datosFacturacion?.razonSocial || 'No especificado')
								}
							</strong>
						</div>
						<div className="col-6">
							<small className="text-muted">Total:</small><br />
							<strong className="text-primary">
								₲ {formatNumberForDisplay(pedido.montoTotal) || '0'}
							</strong>
						</div>
					</div>
				</div>

				{/* Mostrar motivo de cancelación si existe */}
				{pedido.motivoCancelado && (
					<div className="mb-3">
						<strong>Motivo de cancelación original:</strong>
						<div className="p-2 bg-light rounded mt-1">
							<em>"{pedido.motivoCancelado}"</em>
						</div>
					</div>
				)}

				<Alert variant="warning" className="mb-0">
					<small>
						<i className="fa fa-info-circle me-1"></i>
						<strong>Obs:</strong> Al descancelar el pedido, volverá a estar disponible para procesamiento
						y aparecerá en la lista de pedidos actuales.
					</small>
				</Alert>
			</Modal.Body>

			<Modal.Footer>
				<Button
					variant="outline-secondary"
					onClick={handleClose}
					disabled={loading}
				>
					Cancelar
				</Button>
				<Button
					variant="success"
					onClick={handleConfirmar}
					disabled={loading}
				>
					{loading ? (
						<>
							<i className="fa fa-spinner fa-spin me-2"></i>
							Descancelando...
						</>
					) : (
						<>
							<i className="fa fa-undo me-2"></i>
							Confirmar Descancelación
						</>
					)}
				</Button>
			</Modal.Footer>
		</Modal>
	)
}

function PedidosHistorial() {
	const [fetchDeACuantos, setFetchDeACuantos] = useState(PEDIDOS_FETCH_CUANTOS)
	const [numResultados, setNumResultados] = useState(0)
	const [paginaAct, setPaginaAct] = useState(1)
	const [loading, setLoading] = useState(false)

	const [busqueda, setBusqueda] = useState('')
	const [filtroBusqueda, setFiltroBusqueda] = useState('')
	const [filtroTipo, setFiltroTipo] = useState('')
	const [filtroEstado, setFiltroEstado] = useState('')
	const [pedidos, setPedidos] = useState([])

	const [accionActual, setAccionActual] = useState({
		pedido: null,
		accion: ''
	})

	const opcionesCuantosMostrar = OPCIONES_PAGINACION_NUM_RESULTADOS

	const refreshPedidos = async () => {
		try {
			setLoading(true)

			// Construir filtros para historial (pedidos finalizados)
			const filtros = {
				finalizado: true // Para historial: pedidos finalizados
			}

			// Agregar filtro de búsqueda si existe
			if (filtroBusqueda) {
				filtros.busqueda = filtroBusqueda
			}

			// Agregar filtro de tipo si está seleccionado
			if (filtroTipo) {
				filtros.tipo = filtroTipo
			}

			const { datos } = await api.post('a/ver-pedidos', {
				cuantos: fetchDeACuantos,
				desde: paginaAct === 1 ? 0 : (paginaAct - 1) * fetchDeACuantos,
				filtros
			})

			// Procesar los pedidos para calcular estados normalizados
			const pedidosProcesados = (datos.pedidos || []).map(pedido => {
				const estados = calcularEstados(pedido)
				const estadoPrincipal = calcularEstadoPrincipal(pedido)

				return {
					...pedido,
					estados,
					estadoPrincipal
				}
			})

			setPedidos(pedidosProcesados)
			setNumResultados(datos.numResultados || 0)
		} catch (error) {
			toast.error('Error de conexión')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	const handleBusqueda = (value) => {
		setFiltroBusqueda(value || '')
	}

	const debouncedFiltroBusqueda = React.useCallback(
		debounce(handleBusqueda, 400),
		[]
	)

	useEffect(() => {
		debouncedFiltroBusqueda(busqueda)
	}, [busqueda])

	useEffect(() => {
		if (paginaAct === 1) {
			refreshPedidos()
		} else {
			setPaginaAct(1)
		}
	}, [filtroBusqueda, fetchDeACuantos, filtroTipo, filtroEstado])

	useEffect(() => {
		if (!loading) {
			refreshPedidos()
		}
	}, [paginaAct])

	useEffect(() => {
		refreshPedidos()
	}, [])

	const formatearPrecio = (precio) => {
		if (!precio || precio === 0) return "₲ 0"
		return `₲ ${formatNumberForDisplay(precio.toString())}`
	}

	// Función para descancelar pedido
	const descancelarPedido = async (id) => {
		try {
			const { msg } = await api.post(`a/pedidos/${id}/cancelar`, {
				cancelado: false
			})
			refreshPedidos()
			cerrarAccion()
			toast.success(msg || 'Pedido descancelado correctamente')
		} catch (error) {
			console.error('Error al descancelar pedido:', error)
			toast.error('Error al descancelar el pedido')
		}
	}

	const accionesActuales = {
		handleVer: (pedido) => {
			setAccionActual({
				pedido,
				accion: 'ver'
			})
		},
		handleDescancelar: (pedido) => {
			setAccionActual({
				pedido,
				accion: 'descancelar'
			})
		}
	}

	const cerrarAccion = () => {
		setAccionActual({
			pedido: null,
			accion: ''
		})
	}

	const renderEstadoPrincipal = (pedido) => {
		const estadoPrincipal = pedido.estadoPrincipal
		const config = getConfigEstado(estadoPrincipal)

		if (!config) {
			return <Badge bg="secondary">Estado desconocido</Badge>
		}

		return (
			<Badge bg={config.color} className="estado-principal">
				<i className={`fa ${config.icon} me-1`}></i>
				{config.label}
			</Badge>
		)
	}

	const renderEstadoPago = (pedido) => {
		// Usar estados normalizados
		const estados = calcularEstados(pedido)

		if (estados.pagado) {
			return (
				<Badge bg="success" className="estado-pago">
					<i className="fa fa-check me-1"></i>
					Sí
				</Badge>
			)
		} else {
			return (
				<Badge bg="outline-secondary" text="dark" className="estado-pago">
					<i className="fa fa-times me-1"></i>
					No
				</Badge>
			)
		}
	}

	const renderEstadosAdicionales = (pedido) => {
		const estados = []

		// Solo mostrar estados relevantes para el historial
		if (pedido.estados?.entregado) {
			estados.push(
				<Badge key="entregado" bg="success" className="me-1">
					<i className="fa fa-home me-1"></i>
					Entregado
				</Badge>
			)
		}

		return <div className="estados-adicionales">{estados}</div>
	}

	// Función para determinar si un pedido puede ser descancelado
	const puedeDescancelar = (pedido) => {
		// Solo se puede descancelar si está cancelado
		return pedido.estados?.cancelado
	}

	// render
	return (
		<Container fluid id="pedidos-historial" className="vh-100">
			<TopHeader
				iconName="history"
				text="Historial de Pedidos"
			/>

			{/* Filtros */}
			<Row className="d-flex no-gutters mb-3">
				<Col className="mt-2">
					<Form>
						<Row id="searchbar" className="justify-content-between align-items-center g-2">
							<Col sm={6} md={2} style={{ display: 'block' }}>
								<small>
									<span className="d-none d-sm-inline-block">Mostrar de a</span>
									<Form.Select size="sm" value={fetchDeACuantos} onChange={(e) => setFetchDeACuantos(e.target.value)} className="mx-1">
										{opcionesCuantosMostrar.map((o) => (
											<option value={o} key={`mostrar-${o}`}>
												{o}
											</option>
										))}
									</Form.Select>
									<span className="ms-2">pedidos</span>
								</small>
							</Col>

							<Col md={4} lg={3}>
								<Form.Group controlId="txtBusqueda">
									<InputGroup>
										<Form.Control
											autoFocus
											value={busqueda}
											onChange={(e) => setBusqueda(e.target.value)}
											type="text"
											placeholder="Buscar por cliente, ID, teléfono..."
											aria-label="Buscar"
										/>
										<InputGroup.Text><i className="fa fa-search"></i></InputGroup.Text>
									</InputGroup>
								</Form.Group>
							</Col>

							<Col md={2} lg={2}>
								<Form.Group controlId="selectTipo">
									<Form.Select
										value={filtroTipo}
										onChange={(e) => setFiltroTipo(e.target.value)}
									>
										<option value="">Todos los tipos</option>
										{OPCIONES_TIPOS_PEDIDO_PARA_FILTRO.map(tipo => (
											<option key={tipo.value} value={tipo.value}>
												{tipo.label}
											</option>
										))}
									</Form.Select>
								</Form.Group>
							</Col>

							<Col md={2} lg={2}>
								<Form.Group controlId="selectEstado">
									<Form.Select
										value={filtroEstado}
										onChange={(e) => setFiltroEstado(e.target.value)}
									>
										<option value="">Todos los finalizados</option>
										<option value="cancelado">Solo cancelados</option>
										<option value="entregado">Solo entregados</option>
									</Form.Select>
								</Form.Group>
							</Col>
						</Row>
					</Form>
				</Col>
			</Row>

			{/* Tabla de pedidos */}
			<Row>
				<Col>
					<div className="pedidos-tabla">
						<small>Total resultados: {loading ? (<span className="ms-2"><LoadingSpinner size='sm' /></span>) : (<strong>{numResultados}</strong>)}</small>

						<Table striped bordered hover responsive className="mt-2">
							<thead>
								<tr>
									<th>ID</th>
									<th>Cliente</th>
									<th>Tipo</th>
									<th>Estado</th>
									<th>Pagado</th>
									<th>Total</th>
									<th>Fecha</th>
									<th>Acciones</th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr>
										<td colSpan="8" className="text-center py-4">
											<LoadingSpinner />
										</td>
									</tr>
								) : (
									pedidos.length > 0 ? pedidos.map(pedido => (
										<tr key={pedido.id} className={pedido.estados?.cancelado ? 'table-danger' : ''}>
											<td>#{pedido.id}</td>
											<td>
												<div className="cliente-info">
													<div className="cliente-nombre">
														{pedido.usuario ?
															`${pedido.usuario.nombre} ${pedido.usuario.apellido}` :
															(pedido.datosFacturacion?.razonSocial || 'Cliente no especificado')
														}
													</div>
													{pedido.usuario?.telefono && (
														<div className="cliente-telefono">
															<i className="fa fa-phone me-1"></i>
															{pedido.usuario.telefono}
														</div>
													)}
												</div>
											</td>
											<td>
												<Badge bg={getTipoColor(pedido.tipo)} className="badge-tipo">
													{OPCIONES_TIPOS_PEDIDO_PARA_FILTRO.find(t => t.value === pedido.tipo)?.label || pedido.tipo}
												</Badge>
											</td>
											<td>
												{renderEstadoPrincipal(pedido)}
												{renderEstadosAdicionales(pedido)}
											</td>
											<td className="text-center">
												{renderEstadoPago(pedido)}
											</td>
											<td>
												<span className="total-pedido">
													{formatearPrecio(pedido.montoTotal)}
												</span>
											</td>
											<td>
												<span className="fecha-pedido">
													{getDisplayDateTime(pedido.recibido)}
												</span>
											</td>
											<td>
												<DropdownButton
													as="div"
													title={<i className="fa fa-ellipsis-v"></i>}
													id={`dropdown-pedido-${pedido.id}`}
													size="sm"
													variant="outline-secondary"
												>
													<Dropdown.Item onClick={() => accionesActuales.handleVer(pedido)}>
														<i className="fa fa-eye me-2"></i>
														Ver detalles
													</Dropdown.Item>

													{/* Opción para descancelar solo si está cancelado */}
													{puedeDescancelar(pedido) && (
														<>
															<Dropdown.Divider />
															<Dropdown.Item
																onClick={() => accionesActuales.handleDescancelar(pedido)}
																className="text-success"
															>
																<i className="fa fa-undo me-2"></i>
																Descancelar
															</Dropdown.Item>
														</>
													)}
												</DropdownButton>
											</td>
										</tr>
									)) : (
										<tr>
											<td colSpan="8" className="text-center py-4">
												No se encontraron pedidos en el historial con los filtros aplicados.
											</td>
										</tr>
									)
								)}
							</tbody>
						</Table>

						{/* Paginación */}
						{pedidos.length > 0 && (
							<Paginacion
								pagina={paginaAct}
								numPaginas={Math.ceil(numResultados / fetchDeACuantos) || 1}
								setPagina={setPaginaAct}
							/>
						)}
					</div>
				</Col>
			</Row>

			{/* Componente para ver detalles */}
			<VerPedido
				mostrar={accionActual.accion === 'ver'}
				pedido={accionActual.pedido}
				cancelar={cerrarAccion}
				soloLectura={true}
			/>

			{/* Componente para descancelar pedido */}
			<DescancelarPedido
				mostrar={accionActual.accion === 'descancelar'}
				pedido={accionActual.pedido}
				cancelar={cerrarAccion}
				confirmarDescancelacion={descancelarPedido}
			/>
		</Container>
	)
}

export default PedidosHistorial